import { useState } from "react";
import { Loader2, Reply } from "lucide-react";
import { toast } from "sonner";
import { day } from "@/lib/money";
import FilePick from "@/components/FilePick";
import Attachment from "@/components/community/Attachment";

export default function ReplyBox({ api, s, onSaved }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(s.reply || "");
  const [file, setFile] = useState(null);
  const [removeOld, setRemoveOld] = useState(false);
  const [busy, setBusy] = useState(false);
  const has = !!s.replied_at;
  const keepOld = s.reply_attachment && !removeOld;

  const reset = () => { setOpen(false); setText(s.reply || ""); setFile(null); setRemoveOld(false); };
  const save = async () => {
    if (!text.trim() && !file && !keepOld) return toast.error("Write a reply or attach a file.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("reply", text.trim());
      if (file) fd.append("file", file);
      if (removeOld && !file) fd.append("remove_file", "true");
      const { data } = await api.put(`/admin/suggestions/${s.id}/reply`, fd);
      onSaved(s.id, { reply: data.reply, replied_at: data.replied_at, status: data.status, reply_attachment: data.reply_attachment });
      setOpen(false);
      setFile(null);
      setRemoveOld(false);
      toast.success(data.emailed ? "Reply sent and emailed to the client." : "Reply saved. The client will see it in the app.");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not send the reply. Try again.");
    } finally {
      setBusy(false);
    }
    return null;
  };

  return (
    <div className="mt-3" data-testid={`suggestion-reply-${s.id}`}>
      {has && !open && (
        <div className="border-l-2 border-[#FFD600] pl-3 text-sm" data-testid={`suggestion-reply-text-${s.id}`}>
          <div className="text-[11px] text-muted-foreground">Your reply · {day(s.replied_at)}</div>
          {s.reply && <p className="whitespace-pre-wrap break-words">{s.reply}</p>}
          {s.reply_attachment && <Attachment api={api} url={`/suggestions/${s.id}/file?which=reply`} att={s.reply_attachment} testid={`suggestion-reply-att-${s.id}`} />}
        </div>
      )}
      {open ? (
        <div>
          <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} rows={3} data-testid={`suggestion-reply-input-${s.id}`}
            placeholder="Write a short reply the client will see…"
            className="w-full px-3 py-2 bg-[#141414] border border-[#262626] text-white text-base sm:text-sm resize-y focus:outline-none focus:border-white" />
          {keepOld && !file && (
            <div className="mt-2 text-xs text-muted-foreground flex items-center gap-2 min-w-0" data-testid={`suggestion-reply-current-file-${s.id}`}>
              <span className="truncate">Attached: {s.reply_attachment.name}</span>
              <button type="button" onClick={() => setRemoveOld(true)} data-testid={`suggestion-reply-remove-file-${s.id}`} className="shrink-0 text-[#F87171] hover:underline">Remove</button>
            </div>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <FilePick file={file} onFile={setFile} testid={`suggestion-reply-attach-${s.id}`} label={keepOld ? "Replace file" : "Attach photo or PDF"} />
            <button onClick={save} disabled={busy} data-testid={`suggestion-reply-send-${s.id}`}
              className="min-h-[36px] px-4 cta-primary font-mono-label text-[10px] inline-flex items-center gap-2 disabled:opacity-50">
              {busy && <Loader2 size={12} className="animate-spin" />} Send reply
            </button>
            <button onClick={reset} data-testid={`suggestion-reply-cancel-${s.id}`} className="min-h-[36px] text-xs text-muted-foreground hover:text-white">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setOpen(true)} data-testid={`suggestion-reply-open-${s.id}`}
          className="mt-1 min-h-[36px] text-xs text-[#FFD600] inline-flex items-center gap-1.5 hover:underline">
          <Reply size={12} /> {has ? "Edit reply" : "Reply"}
        </button>
      )}
    </div>
  );
}
